import { useQueryClient, useQuery, useMutation } from "react-query";
import { useAlert } from "./alertHooks";
import {
  createUser,
  fetchUsers,
  fetchMe,
  fetchMeGlobal,
  updateUser,
  updateUserEnable,
  updateUserDisable,
  updateResetPassword,
  fetchBuckets,
  fetchRoles,
  deleteUser,
  fetchActivityLog,
} from "../api/user";

export const useFetchUsers = () => {
  const { addAlert } = useAlert();
  return useQuery(["users"], fetchUsers, {
    onError: (error: any) => {
      console.error("Failed to fetch users:", error);
      addAlert("Failed to load users. Please refresh the page.", "error");
    },
  });
};

export const useFetchMe = () => {
  const { addAlert } = useAlert();
  return useQuery(["me"], fetchMe, {
    onError: (error: any) => {
      console.error("Failed to fetch user info:", error);
      addAlert("Failed to load user information", "error");
    },
  });
};

// Region-agnostic profile for the generic-dashboard landing. Intentionally does
// NOT use useAlert: this runs above AlertProvider in the App tree.
export const useFetchMeGlobal = (enabled: boolean = true) => {
  const shop =
    typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("shop");
  return useQuery(["me-global", shop], () => fetchMeGlobal(shop), {
    enabled,
    retry: false,
    onError: (error: any) => {
      console.error("Failed to fetch global user info:", error);
    },
  });
};

export const useFetchBuckets = (enabled: boolean = true) => {
  const { addAlert } = useAlert();
  return useQuery(["buckets"], fetchBuckets, {
    enabled,
    // Match BucketService's 30s stage-view TTL. Publish still invalidates.
    staleTime: 30 * 1000,
    // Keep the list past the 5 minute default. Callers derive which panels a stage
    // may show from this, so an evicted entry means a window-focus refetch hands
    // them `undefined` and the permitted panels briefly collapse to the default —
    // which tore the grading toolbar, and any tool open above it, off the screen.
    cacheTime: 60 * 60 * 1000,
    // Buckets change when someone republishes the workflow, not while a grader is
    // tabbed away. Refetching on focus bought nothing and cost a re-render mid-measurement.
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      console.error("Failed to fetch buckets:", error);
      addAlert("Failed to load buckets", "error");
    },
  });
};

export const useFetchRoles = () => {
  const { addAlert } = useAlert();
  return useQuery(["roles"], fetchRoles, {
    onError: (error: any) => {
      console.error("Failed to fetch roles:", error);
      addAlert("Failed to load roles", "error");
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { addAlert } = useAlert();
  const { isLoading: isMutating, ...rest } = useMutation(
    (user: User) => createUser(user),
    {
      onSuccess: (createdUser: User) => {
        queryClient.setQueryData<User[]>(["users"], (prev): User[] => {
          if (prev) {
            return [...prev, createdUser];
          }
          return [createdUser];
        });
        addAlert("User created successfully", "success");
      },
      onError: (error: any) => {
        console.error("Failed to create user:", error);
        addAlert("Failed to create user. Please try again.", "error");
      },
    }
  );
  return { isMutating, ...rest };
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { addAlert } = useAlert();
  const { isLoading: isMutating, ...rest } = useMutation(
    (user: User) => updateUser(user),
    {
      onSuccess: (updatedUser: User, update: User) => {
        // Update cache with response
        queryClient.setQueryData<User[]>(["users"], (prev): User[] => {
          if (prev) {
            return prev.map((user) =>
              user.id === update.id ? updatedUser : user
            );
          }
          return [updatedUser];
        });
        
        // Invalidate users query to force refetch from backend
        queryClient.invalidateQueries(["users"]);
        
        // Refetch current user if they updated themselves
        queryClient.invalidateQueries(["me"]);
        
        addAlert("User updated successfully", "success");
      },
      onError: (error: any) => {
        console.error("Failed to update user:", error);
        addAlert("Failed to update user. Please try again.", "error");
      },
    }
  );
  return { isMutating, ...rest };
};

export const useEnableUser = () => {
  const queryClient = useQueryClient();
  const { addAlert } = useAlert();
  const { isLoading: isMutating, ...rest } = useMutation(
    (userId: number) => updateUserEnable(userId),
    {
      onSuccess: (updatedUser: User, userId: number) => {
        queryClient.setQueryData<User[]>(["users"], (prev): User[] => {
          if (prev) {
            return prev.map((user) =>
              user.id === userId ? updatedUser : user
            );
          }
          return [updatedUser];
        });
        addAlert("User enabled successfully", "success");
      },
      onError: (error: any) => {
        console.error("Failed to enable user:", error);
        addAlert("Failed to enable user. Please try again.", "error");
      },
    }
  );
  return { isMutating, ...rest };
};

export const useDisableUser = () => {
  const queryClient = useQueryClient();
  const { addAlert } = useAlert();
  const { isLoading: isMutating, ...rest } = useMutation(
    (userId: number) => updateUserDisable(userId),
    {
      onSuccess: (updatedUser: User, userId: number) => {
        queryClient.setQueryData<User[]>(["users"], (prev): User[] => {
          if (prev) {
            return prev.map((user) =>
              user.id === userId ? updatedUser : user
            );
          }
          return [updatedUser];
        });
        addAlert("User disabled successfully", "success");
      },
      onError: (error: any) => {
        console.error("Failed to disable user:", error);
        addAlert("Failed to disable user. Please try again.", "error");
      },
    }
  );
  return { isMutating, ...rest };
};

export const useResetPassword = () => {
  const { addAlert } = useAlert();
  const { isLoading: isMutating, ...rest } = useMutation(
    (userId: number) => updateResetPassword(userId),
    {
      onSuccess: () => {
        addAlert("Password reset successfully", "success");
      },
      onError: (error: any) => {
        console.error("Failed to reset password:", error);
        addAlert("Failed to reset password. Please try again.", "error");
      },
    }
  );
  return { isMutating, ...rest };
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { isLoading: isMutating, ...rest } = useMutation(
    (userId: number) => deleteUser(userId),
    {
      onSuccess: (response: GenericResponse, userId: number) => {
        queryClient.setQueryData<User[]>(["users"], (prev): User[] => {
          if (prev) {
            return prev.filter((user) => user.id !== userId);
          }
          return [];
        });
      },
    }
  );
  return { isMutating, ...rest };
};

export const useFetchActivityLog = (
  limit: number = 50,
  startDate?: string,
  endDate?: string,
  userId?: number,
  orderName?: string,
  gradingId?: number,
  enabled: boolean = true
) => {
  return useQuery(
    ["activityLog", { limit, startDate, endDate, userId, orderName, gradingId }],
    () => fetchActivityLog(limit, startDate, endDate, userId, orderName, gradingId),
    {
      refetchInterval: 30000,
      enabled,
    }
  );
};
